import { TransactionStoreModel } from "../../../../models/store/transaction.store.model";
import { Transaction } from "../../../../models/data/blobby/transaction.data";
import { AllocationUtils } from "@bitwarden/web-vault/app/models/store/allocations/allocation.utils";
import { TransactionDirection } from "@bitwarden/web-vault/app/models/enum/transactionDirection";

type TransactionModelV8 = Omit<TransactionStoreModel, "v"> & {
  v: 8;
};

type TransactionModelV7 = Omit<TransactionModelV8, "v" | "xLid"> & {
  v: 7;
};

type TransactionModelV6 = Omit<TransactionModelV7, "v" | "pDt"> & {
  v: 6;
};

type TransactionModelV5 = Omit<TransactionModelV6, "v" | "alc"> & {
  v: 5;
};

type TransactionModelV4 = Omit<TransactionModelV5, "v"> & {
  v: 4;
};

type TransactionModalV3 = Omit<TransactionModelV4, "v" | "ori"> & {
  v: 3;
};

type TransactionModelV2 = Omit<TransactionModalV3, "v" | "sts"> & {
  v: 2;
};

type TransactionModelV1 = Omit<TransactionModelV2, "v" | "bIBO"> & {
  v: 1;
};

export const transaction_v0_to_v1 = (transaction: Transaction): TransactionModelV1 => {
  return {
    acId: transaction.accountId,
    bIB: transaction.bankImportedBalance,
    dc: transaction.dateCreated,
    dfn: transaction.definition,
    dir: transaction.direction,
    dm: new Date().toISOString(),
    dsc: transaction.description,
    id: transaction.id,
    knd: transaction.kind,
    lTid: transaction.linkedTo,
    qty: {
      cvrt: transaction.quantity.convrate,
      cvsym: transaction.quantity.convsym,
      ccy: transaction.quantity.currency,
      qty: {
        amt: transaction.quantity.actualQuantity.amount,
        prcs: transaction.quantity.actualQuantity.precision,
        sym: transaction.quantity.actualQuantity.symbol,
      },
    },
    srId: transaction.sourceId,
    tDt: {
      date: transaction.transactionDate.date.toDateString(),
      time: transaction.transactionDate.time,
      timeZone: transaction.transactionDate.timeZone,
      reliableTimeSource: false,
    },
    v: 1,
    vid: "",
    vlPc: transaction.valuationPrice,
  };
};

export const transaction_v1_to_v2 = (transaction: TransactionStoreModel): TransactionModelV2 => {
  return {
    ...transaction,
    bIBO: null,
    v: 2,
  };
};

export const transaction_v2_to_v3 = (transaction: TransactionStoreModel): TransactionModalV3 => {
  return {
    ...transaction,
    sts: "posted",
    v: 3,
  };
};

export const transaction_v3_to_v4 = (transaction: TransactionStoreModel): TransactionModelV4 => {
  return {
    ...transaction,
    ori: null,
    v: 4,
  };
};

export const transaction_v4_to_v5 = (transaction: TransactionStoreModel): TransactionModelV5 => {
  if (transaction.tDt.timeZone === null) {
    transaction.tDt.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone; // Default to UTC if no timezone is provided
  }
  if (transaction.tDt.reliableTimeSource === undefined) {
    transaction.tDt.reliableTimeSource = false; // Default to true if not specified
  }
  return {
    ...transaction,
    v: 5,
  };
};

export const transaction_v5_to_v6 = (transaction: TransactionStoreModel): TransactionModelV6 => {
  // set the transactions to have default allocation if it does not have allocations
  if (
    transaction.alc === null ||
    (Array.isArray(transaction.alc) && transaction.alc.length === 0) ||
    transaction.alc === undefined
  ) {
    let amount = transaction.qty?.qty?.amt ?? 0;
    if (transaction.dir === TransactionDirection.Out && amount > 0) {
      amount = amount * -1;
    }
    transaction.alc = [AllocationUtils.createDefaultAllocationAsObject(amount)];
  }
  return {
    ...transaction,
    v: 6,
  };
};

export const transaction_v6_to_v7 = (transaction: TransactionStoreModel): TransactionModelV7 => {
  return {
    ...transaction,
    /* Previous versions had tDt as the post date. they will have the same tDt and pDt in v7. */
    pDt: transaction.tDt,
    v: 7,
  };
};

export const transaction_v7_to_v8 = (transaction: TransactionStoreModel): TransactionModelV8 => {
  return {
    ...transaction,
    /* v8 adds xLid: ids the user has explicitly unlinked from this transaction. */
    xLid: transaction.xLid ?? [],
    v: 8,
  };
};

export const transaction_v8_to_v9 = (transaction: TransactionModelV8): TransactionStoreModel => {
  /* v9 fixes gloss-credits (CrS) transactions synced with convrate hardcoded to 1
     instead of left unset, which threw off reval/balance calculations. */
  const cvrt = transaction.id?.startsWith("gloss-credits-") ? null : transaction.qty.cvrt;

  return {
    ...transaction,
    qty: {
      ...transaction.qty,
      cvrt,
    },
    v: 9,
  };
};
